/**
 * The `*.panluma.ai` DNS zone we control.
 *
 * Wildcard DNS for the zone points at our single CloudFront distribution, so a
 * host inside it can never be owned by an attacker. That makes "is this host in
 * our zone?" the one safe predicate for deciding whether a URL taken from data
 * (a `?domain=` hint, a widget's captured `page_url`) may influence navigation.
 *
 * Fully custom tenant domains (`crm.acme.com`) are deliberately NOT in the zone:
 * we cannot validate them client-side, so they are treated as foreign.
 */

const PANLUMA_ZONE_SUFFIX = '.panluma.ai';

/**
 * The marketing site. It lives in the zone but is a different application
 * (Astro, not the SPA), so its paths never resolve to an app route.
 */
const MARKETING_HOST = 'www.panluma.ai';

/**
 * A bare hostname: lowercase letters/digits/hyphens/dots only — no `/`, `@`,
 * `:`, or whitespace that could escape the host position of a target URL.
 * Callers passing a raw, user-supplied hint must lowercase and trim it first;
 * a `URL.hostname` is already normalized.
 */
const BARE_HOSTNAME_RE = /^[a-z0-9.-]+$/;

/** Whether `hostname` is a host inside the `*.panluma.ai` zone we control. */
export function isPanlumaZoneHost(hostname: string): boolean {
  return BARE_HOSTNAME_RE.test(hostname) && hostname.endsWith(PANLUMA_ZONE_SUFFIX);
}

/** Whether `hostname` serves the PanLuma SPA (zone host, excluding marketing). */
export function isPanlumaAppHost(hostname: string): boolean {
  return isPanlumaZoneHost(hostname) && hostname !== MARKETING_HOST;
}
